import { FC, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Chip, IconButton, Popover, Stack, Typography } from '@mui/material'

import { EventKpiPeriodExplanation } from '../../helpers/calculateEventPerformanceChanges'
import { EventKPIIndicator } from '../EventKPIIndicator/EventKPIIndicator'

type Props = {
  explanation?: EventKpiPeriodExplanation
  revenueChange?: number
  downloadChange?: number
  dauChange?: number
  eventStart: number
  eventEnd: number
}

const EventKpiExplanationFullDetail: FC<Props> = ({ explanation }) => {
  const { t } = useTranslation()

  if (!explanation) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t('live-events:kpi_explanation_title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('live-events:kpi_explanation_ongoing_summary')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {t('live-events:kpi_explanation_title')}
      </Typography>
      {explanation.variant === 'multi_day_average' && (
        <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
          {t('live-events:kpi_explanation_multi_day_intro')}
        </Typography>
      )}
      {explanation.variant === 'multi_day_average' ? (
        <>
          {explanation.ongoing && (
            <Typography variant="caption" component="div" sx={{ mb: 1, display: 'block', opacity: 0.95 }}>
              {t('live-events:kpi_explanation_ongoing_note')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5, mb: 1 }}>
            <Typography variant="body2" component="span">
              <strong>{t('live-events:kpi_explanation_during_label')}</strong> {explanation.duringRangeFormatted}
            </Typography>
            {explanation.ongoing && (
              <Chip
                label={t('live-events:kpi_explanation_status_in_progress')}
                size="small"
                sx={{
                  fontSize: '10px',
                  height: '18px',
                  fontWeight: 'bold',
                  bgcolor: 'warning.main',
                  color: 'white',
                  flexShrink: 0,
                  mb: 0,
                }}
              />
            )}
          </Box>
          <Typography variant="body2" component="div" sx={{ mb: 0 }}>
            <strong>{t('live-events:kpi_explanation_before_label')}</strong> {explanation.beforeRangeFormatted}
          </Typography>
        </>
      ) : (
        <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
          {t('live-events:kpi_explanation_single_day', { date: explanation.eventDayFormatted })}
        </Typography>
      )}
    </Box>
  )
}

export const EventKpiExplanationCallout: FC<Props> = (props) => {
  const { t } = useTranslation()
  const { revenueChange, downloadChange, dauChange, eventStart, eventEnd } = props
  const anchorRef = useRef<HTMLDivElement>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const hasData = revenueChange !== undefined || downloadChange !== undefined || dauChange !== undefined

  return (
    <>
      <Box
        sx={{
          mt: 2,
          mb: 3,
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Box
          ref={anchorRef}
          sx={{
            display: 'inline-flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <IconButton
            size="small"
            color="info"
            aria-label={t('live-events:kpi_explanation_title')}
            onClick={() => setAnchorEl((prev) => (prev ? null : anchorRef.current))}
            sx={{ mt: -1 }}
          >
            <InfoOutlined />
          </IconButton>
          {hasData ? (
            <EventKPIIndicator
              revenueChange={revenueChange}
              downloadChange={downloadChange}
              dauChange={dauChange}
              startTime={eventStart}
              endTime={eventEnd}
              showComparisonRange
              showYearInComparison
              hideStatusChip
              showAllKpiChips
            />
          ) : (
            <Stack direction="column" alignItems="flex-start" spacing={0.5}>
              <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 'bold' }}>
                {t('live-events:kpi_explanation_ongoing_summary')}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Chip label="REV" color="default" size="small" sx={{ fontWeight: 'bold', fontSize: '11px' }} />
                <Chip label="DL" color="default" size="small" sx={{ fontWeight: 'bold', fontSize: '11px' }} />
                <Chip label="DAU" color="default" size="small" sx={{ fontWeight: 'bold', fontSize: '11px' }} />
              </Stack>
            </Stack>
          )}
        </Box>
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 440,
              maxHeight: '72vh',
              overflow: 'auto',
              p: 2,
            },
          },
        }}
      >
        <EventKpiExplanationFullDetail {...props} />
      </Popover>
    </>
  )
}
